import React, { ReactElement, useState, useMemo } from "react";
import { Redirect } from "react-router-dom";
import { useQuery } from "@apollo/client";
import MyCellarReviewTray from "../MyCellarReviewTray";

import { GET_AUTH } from "../../../graphql/resolvers/auth";
import { FETCH_POLICY_CACHE_ONLY, WINE_LIST_SLUGS } from "../../../helpers/constants";
import { GET_WINE, GET_WINE_RATING } from "../../../graphql/queries";
import urlPatterns from "../../../urls";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import { Credit } from "../../../types/__generated__/Credit";

import Loading from "../../atoms/Loading";
import OffCanvasTray from "../OffCanvasTray";
import BottleInfoTray from "../BottleInfoTray";
import { useDeviceSize } from "../../../helpers/hooks";
import { GetWineRating } from "../../../types/__generated__/GetWineRating";
import { GetWine } from "../../../types/__generated__/GetWine";
import { getAuth } from "../../../types/__generated__/getAuth";
import { StyledBoxTray, loadingStyle } from "./styles";
import { WineDetailsContext } from "./context";

interface WineDetailsTrayProps {
  forceAddToBox?: boolean;
  isAddingAnother?: boolean;
  isInBoxAndCartTray?: boolean;
  slug: string | null | undefined;
  memberShoppingCartQuery: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  handleToggleBottleInfoTray: Function;
  disableScrollLockInSideTray?: boolean;
  isFromSavedList?: boolean;
  addBottleToCredit?: Credit;
  locationId?: number;
}

const WineDetailsTray = ({
  forceAddToBox = false,
  isAddingAnother = false,
  isInBoxAndCartTray = false,
  slug,
  memberShoppingCartQuery,
  handleToggleBottleInfoTray,
  disableScrollLockInSideTray = false,
  isFromSavedList = false,
  addBottleToCredit,
  locationId,
}: WineDetailsTrayProps) => {
  const [hide, setHide] = useState<boolean>(false);
  const [shouldShowReviewTray, setShouldShowReviewTray] = useState<boolean>(false);

  const { isSmall } = useDeviceSize();

  const { data: authData, loading: authLoading } = useQuery<getAuth>(GET_AUTH, {
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });

  const memberId = authData?.auth?.memberId;

  const { data: wineData, loading: wineLoading } = useQuery<GetWine>(GET_WINE, {
    variables: { slug, memberId },
    partialRefetch: true,
    skip: !slug,
  });

  const { data: wineRatingData, loading: wineRatingLoading } = useQuery<GetWineRating>(GET_WINE_RATING, {
    skip: !wineData?.wine?.id || !memberId,
    variables: { wineId: wineData?.wine?.id, memberId },
    partialRefetch: true,
  });

  /**
   * this function sets the state of hide to true,
   * so, the hiding action can start animating,
   * then, close the tray after 200ms.
   * */
  const handleCloseTray = () => {
    setHide(true);

    if (isSmall && isFromSavedList) {
      handleToggleBottleInfoTray();
      return;
    }
    setTimeout(() => handleToggleBottleInfoTray(), 200);
  };

  const value = useMemo(
    () => ({
      closeWineDetailsTray: handleCloseTray,
    }),
    []
  );

  const handleReviewWineClick = () => {
    setShouldShowReviewTray(true);
    setHide(true);
  };

  const handleReviewWineTrayClose = () => {
    setShouldShowReviewTray(false);
    setHide(false);
  };

  const loading = authLoading || wineLoading || wineRatingLoading;

  // Defer the redirect decision until the wine query has resolved — without
  // this guard the tray would mount with wineData undefined, hit the !== null
  // branch as `wineData?.wine === undefined` (falsy but not strictly null) and
  // skip the redirect. Once loading completes, wine === null means not-found.
  if (!loading && wineData?.wine === null) {
    return <Redirect to={urlPatterns.WINE_LIST(WINE_LIST_SLUGS.ALL_WINES)} />;
  }

  // Render the OffCanvasTray immediately so the slide-in animation starts on
  // click rather than waiting for GET_WINE + GET_WINE_RATING to resolve. The
  // spinner sits inside the tray while data is in-flight; BottleInfoTray
  // swaps in once `wineData.wine` is populated.
  const renderWineDetailsTray = (): ReactElement => (
    <OffCanvasTray
      setOverflowHidden={false}
      isOffcanvasOpened
      outSideClickHandler={handleCloseTray}
      disableScrollLockInSideTray={disableScrollLockInSideTray}
    >
      <StyledBoxTray isOpened hide={hide} isInBoxAndCartTray={!!isInBoxAndCartTray}>
        {loading || !wineData?.wine ? (
          <Loading style={loadingStyle} />
        ) : (
          <BottleInfoTray
            isInBoxAndCartTray={isInBoxAndCartTray}
            forceAddToBox={forceAddToBox}
            isAddingAnother={isAddingAnother}
            addBottleToCredit={addBottleToCredit}
            handleToggleBottleInfoTray={handleCloseTray}
            wine={wineData.wine}
            hasWineBeenReview={!!wineRatingData?.wineRating?.note}
            onReviewWineClick={handleReviewWineClick}
            memberId={memberId}
            memberShoppingCartQuery={memberShoppingCartQuery}
            locationId={locationId}
          />
        )}
      </StyledBoxTray>
    </OffCanvasTray>
  );

  const renderMyCellarReviewTray = (): ReactElement => (
    <MyCellarReviewTray
      selectedWine={wineData?.wine}
      onCloseButtonClick={() => handleReviewWineTrayClose()}
      isOpened={shouldShowReviewTray}
      // TODO: Find solution for adding setRandomVariableRewards
      setRandomVariableRewards={() => {}}
      isFromSavedList
    />
  );

  return (
    <WineDetailsContext.Provider value={value}>
      {shouldShowReviewTray ? renderMyCellarReviewTray() : renderWineDetailsTray()}
    </WineDetailsContext.Provider>
  );
};

export default WineDetailsTray;
